import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { format, isValid, parse } from "date-fns";
import {
  AH_ACCENT,
  AH_BORDER,
  AH_MUTED,
  AH_TEXT,
  AH_TRACK,
  AH_WHITE,
  auditHubPageSx,
  outlinedButtonSx,
  primaryButtonSx,
} from "../Compliance/AuditHub/theme";
import {
  formTableActionBtnSx,
  formTableActionsWrapSx,
  formTableBodyCellSx,
  formTableCardSx,
  formTableDeleteBtnSx,
  formTableHeadCellSx,
} from "./formTableStyles";

/** Same status tones as ProgramsGrid / Audit Hub chips. */
const STATUS_TONE = {
  ACTIVE: { bg: "#ecfdf5", fg: "#047857" },
  DRAFT: { bg: "#fffbeb", fg: "#b45309" },
  RETIRED: { bg: "#eff6ff", fg: "#2563eb" },
  ARCHIVED: { bg: "#f1f5f9", fg: "#475569" },
};

const COLUMNS = [
  { key: "number", label: "TASK ORDER", width: "16%" },
  { key: "status", label: "STATUS", width: "10%" },
  { key: "start", label: "START", width: "11%" },
  { key: "end", label: "END", width: "11%" },
  { key: "officer", label: "CONTRACTING OFFICER", width: "18%" },
  { key: "commitment", label: "COMMITMENT", width: "14%" },
  { key: "accounts", label: "AWS ACCOUNTS", width: "12%" },
  { key: "actions", label: "ACTIONS", width: "8%" },
];

const formatDate = (value) => {
  if (!value) return "—";
  const parsed = parse(String(value).slice(0, 10), "yyyy-MM-dd", new Date());
  return isValid(parsed) ? format(parsed, "MM/dd/yyyy") : String(value);
};

/** Format total_obligated_amount as Commitment Amount for the grid. */
const formatCommitmentAmount = (value) => {
  if (value == null || value === "") return "—";
  const cleaned = String(value).replace(/[$,]/g, "").trim();
  if (!cleaned) return "—";
  const amount = Number(cleaned);
  if (!Number.isFinite(amount)) return String(value);
  return `$${amount.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

/** Distinct 12-digit AWS account IDs on a task-order list/detail record. */
const getTaskOrderAccounts = (order) => {
  if (!order) return [];
  const raw = Array.isArray(order.accountNumbers)
    ? order.accountNumbers
    : Array.isArray(order.aws_account_numbers)
      ? order.aws_account_numbers
      : [];
  const seen = new Set();
  const out = [];
  raw.forEach((id) => {
    const n = String(id || "").trim();
    if (!/^\d{12}$/.test(n) || seen.has(n)) return;
    seen.add(n);
    out.push(n);
  });
  return out;
};

const statusChipSx = (status) => {
  const tone = STATUS_TONE[status] || STATUS_TONE.ARCHIVED;
  return {
    height: 22,
    fontSize: "0.75rem",
    fontWeight: 600,
    borderRadius: "6px",
    backgroundColor: tone.bg,
    color: tone.fg,
    "& .MuiChip-label": { px: 1 },
  };
};

const TaskOrdersDialog = ({
  open,
  onClose,
  customer: _customer,
  taskOrders,
  loading,
  error,
  onEdit,
  onDelete,
  deletingId,
}) => {
  const [pendingArchive, setPendingArchive] = useState(null);
  const [accountsTarget, setAccountsTarget] = useState(null);
  const archiveBusy = Boolean(pendingArchive?.id && deletingId === pendingArchive.id);
  const pendingLabel = pendingArchive?.task_order_number || "this workorder";
  const accountsDialogList = useMemo(
    () => getTaskOrderAccounts(accountsTarget),
    [accountsTarget]
  );

  useEffect(() => {
    if (!open) {
      setPendingArchive(null);
      setAccountsTarget(null);
    }
  }, [open]);

  useEffect(() => {
    if (!pendingArchive?.id) return;
    const stillPresent = (taskOrders || []).some((order) => order.id === pendingArchive.id);
    if (!stillPresent && deletingId == null) {
      setPendingArchive(null);
    }
  }, [taskOrders, pendingArchive, deletingId]);

  const closeArchiveConfirm = () => {
    if (archiveBusy) return;
    setPendingArchive(null);
  };

  const confirmArchive = () => {
    if (!pendingArchive || archiveBusy) return;
    onDelete(pendingArchive);
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          ...auditHubPageSx,
          borderRadius: "12px",
          overflow: "hidden",
          maxWidth: 1120,
          width: "100%",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          minHeight: 56,
          px: 3,
          backgroundColor: AH_ACCENT,
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: "1.0625rem", color: AH_WHITE, lineHeight: 1.3, minWidth: 0 }}>
          Task Orders
        </Typography>
        <Box
          component="button"
          type="button"
          aria-label="Close"
          onClick={onClose}
          sx={{
            p: 0,
            m: 0,
            border: "none",
            background: "transparent",
            color: AH_WHITE,
            fontSize: 28,
            fontWeight: 700,
            lineHeight: 1,
            cursor: "pointer",
            flexShrink: 0,
          }}
        >
          ×
        </Box>
      </Box>

      <DialogContent
        sx={{
          px: { xs: 1.5, sm: 2.5 },
          py: { xs: 1.5, sm: 2.5 },
          backgroundColor: AH_TRACK,
        }}
      >
        {error ? (
          <Typography sx={{ mb: 1.5, fontSize: 13, color: "#d32f2f" }} role="alert">
            {error}
          </Typography>
        ) : null}

        <TableContainer sx={{ ...formTableCardSx, maxHeight: 520, overflow: "auto" }}>
          <Table
            size="small"
            stickyHeader
            aria-label="Customer workorders"
            sx={{ minWidth: 880, tableLayout: "fixed", width: "100%" }}
          >
            <TableHead>
              <TableRow>
                {COLUMNS.map((column) => (
                  <TableCell
                    key={column.key}
                    sx={{ ...formTableHeadCellSx, width: column.width, px: 1 }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    align="center"
                    sx={{ ...formTableBodyCellSx, py: 6, borderBottom: 0 }}
                  >
                    <CircularProgress size={24} sx={{ color: AH_ACCENT }} />
                  </TableCell>
                </TableRow>
              ) : taskOrders.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    sx={{ ...formTableBodyCellSx, py: 6, borderBottom: 0, color: AH_MUTED }}
                  >
                    No workorders for this customer yet.
                  </TableCell>
                </TableRow>
              ) : (
                taskOrders.map((order) => {
                  const accounts = getTaskOrderAccounts(order);
                  const accountCount = accounts.length;
                  const accountsDisabled = accountCount === 0;
                  return (
                  <TableRow key={order.id} hover>
                    <TableCell
                      sx={{
                        ...formTableBodyCellSx,
                        fontWeight: 600,
                        color: AH_TEXT,
                        px: 1,
                        overflowWrap: "anywhere",
                      }}
                    >
                      {order.task_order_number || "—"}
                    </TableCell>
                    <TableCell sx={{ ...formTableBodyCellSx, px: 1 }}>
                      <Chip size="small" label={order.status || "—"} sx={statusChipSx(order.status)} />
                    </TableCell>
                    <TableCell sx={{ ...formTableBodyCellSx, px: 1, whiteSpace: "nowrap" }}>
                      {formatDate(order.start_date)}
                    </TableCell>
                    <TableCell sx={{ ...formTableBodyCellSx, px: 1, whiteSpace: "nowrap" }}>
                      {formatDate(order.end_date)}
                    </TableCell>
                    <TableCell
                      sx={{
                        ...formTableBodyCellSx,
                        px: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={String(order.contracting_officer_name || "").trim() || undefined}
                    >
                      {String(order.contracting_officer_name || "").trim() || "—"}
                    </TableCell>
                    <TableCell sx={{ ...formTableBodyCellSx, px: 1, whiteSpace: "nowrap" }}>
                      {formatCommitmentAmount(order.total_obligated_amount)}
                    </TableCell>
                    <TableCell sx={{ ...formTableBodyCellSx, px: 1 }}>
                      <Tooltip
                        title={
                          accountsDisabled
                            ? "No AWS accounts"
                            : `View ${accountCount} AWS account${accountCount === 1 ? "" : "s"}`
                        }
                      >
                        <Box
                          component="button"
                          type="button"
                          disabled={accountsDisabled}
                          aria-label={
                            accountsDisabled
                              ? `No AWS accounts for ${order.task_order_number || "task order"}`
                              : `View ${accountCount} AWS accounts for ${order.task_order_number || "task order"}`
                          }
                          onClick={() => {
                            if (!accountsDisabled) setAccountsTarget(order);
                          }}
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 32,
                            height: 32,
                            p: 0,
                            m: 0,
                            borderRadius: "50%",
                            border: accountsDisabled ? "1px solid #e2e8f0" : "1px solid #3b82f6",
                            backgroundColor: accountsDisabled ? "#f3f4f6" : "#f0f9ff",
                            opacity: accountsDisabled ? 0.6 : 1,
                            cursor: accountsDisabled ? "not-allowed" : "pointer",
                            transition: "all 0.2s ease-in-out",
                            ...(accountsDisabled
                              ? {}
                              : {
                                  "&:hover": {
                                    backgroundColor: "#dbeafe",
                                    borderColor: "#2563eb",
                                    boxShadow: "0 1px 3px rgba(59, 130, 246, 0.2)",
                                  },
                                }),
                          }}
                        >
                          <Typography
                            component="span"
                            sx={{
                              fontWeight: 600,
                              color: accountsDisabled ? "#9ca3af" : "#3b82f6",
                              fontSize: "0.75rem",
                              lineHeight: 1,
                            }}
                          >
                            {accountCount}
                          </Typography>
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ ...formTableBodyCellSx, px: 1, whiteSpace: "nowrap" }}>
                      <Box sx={formTableActionsWrapSx}>
                        <Tooltip title="Edit workorder">
                          <IconButton
                            size="small"
                            aria-label={`Edit ${order.task_order_number || "task order"}`}
                            onClick={() => onEdit(order)}
                            sx={formTableActionBtnSx}
                          >
                            <EditOutlinedIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete workorder">
                          <span>
                            <IconButton
                              size="small"
                              aria-label={`Delete ${order.task_order_number || "task order"}`}
                              disabled={deletingId === order.id || Boolean(pendingArchive)}
                              onClick={() => setPendingArchive(order)}
                              sx={formTableDeleteBtnSx}
                            >
                              {deletingId === order.id ? (
                                <CircularProgress size={14} sx={{ color: "#fca5a5" }} />
                              ) : (
                                <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          px: 3,
          py: 2,
          borderTop: `1px solid ${AH_BORDER}`,
          backgroundColor: "#f8fafc",
        }}
      >
        <Button
          variant="contained"
          onClick={onClose}
          sx={{
            ...outlinedButtonSx,
            height: 36,
            minHeight: 36,
            backgroundColor: "#74788d",
            color: AH_WHITE,
            borderColor: "#74788d",
            "&:hover": { backgroundColor: "#636678", borderColor: "#636678", boxShadow: "none" },
          }}
        >
          Close
        </Button>
      </Box>
    </Dialog>

    <Dialog
      open={Boolean(accountsTarget)}
      onClose={() => setAccountsTarget(null)}
      fullWidth
      maxWidth="sm"
      scroll="paper"
      aria-labelledby="task-order-accounts-dialog-title"
      PaperProps={{
        sx: {
          ...auditHubPageSx,
          borderRadius: "12px",
          overflow: "hidden",
          maxWidth: 560,
          maxHeight: { xs: "92vh", sm: "88vh" },
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          minHeight: 56,
          px: 3,
          flexShrink: 0,
          backgroundColor: AH_ACCENT,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            id="task-order-accounts-dialog-title"
            sx={{ fontWeight: 700, fontSize: "1.0625rem", color: AH_WHITE, lineHeight: 1.3 }}
          >
            AWS Accounts
          </Typography>
          <Typography
            sx={{
              mt: 0.25,
              fontSize: 12,
              color: AH_WHITE,
              opacity: 0.92,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {accountsTarget?.task_order_number || "Task order"}
            {accountsDialogList.length
              ? ` · ${accountsDialogList.length} account${accountsDialogList.length === 1 ? "" : "s"}`
              : ""}
          </Typography>
        </Box>
        <Box
          component="button"
          type="button"
          aria-label="Close"
          onClick={() => setAccountsTarget(null)}
          sx={{
            p: 0,
            m: 0,
            border: "none",
            background: "transparent",
            color: AH_WHITE,
            fontSize: 28,
            fontWeight: 700,
            lineHeight: 1,
            cursor: "pointer",
            flexShrink: 0,
          }}
        >
          ×
        </Box>
      </Box>
      <DialogContent
        sx={{
          px: 3,
          py: 2.5,
          backgroundColor: AH_WHITE,
          flex: "1 1 auto",
          overflowY: "auto",
        }}
      >
        {accountsDialogList.length === 0 ? (
          <Typography sx={{ fontSize: 13, color: AH_MUTED }}>
            No AWS accounts on this task order.
          </Typography>
        ) : (
          <Box
            component="ul"
            sx={{
              m: 0,
              pl: 0,
              listStyle: "none",
              display: "flex",
              flexDirection: "column",
              gap: 0.75,
            }}
          >
            {accountsDialogList.map((accountNumber) => (
              <Box
                component="li"
                key={accountNumber}
                sx={{
                  px: 1.5,
                  py: 1,
                  borderRadius: "8px",
                  border: `1px solid ${AH_BORDER}`,
                  backgroundColor: "#f8fafc",
                  fontSize: "0.8125rem",
                  fontWeight: 500,
                  fontFamily: "monospace",
                  color: AH_TEXT,
                  lineHeight: 1.45,
                }}
              >
                {accountNumber}
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          px: 3,
          py: 2,
          borderTop: `1px solid ${AH_BORDER}`,
          backgroundColor: "#f8fafc",
          flexShrink: 0,
        }}
      >
        <Button
          variant="contained"
          onClick={() => setAccountsTarget(null)}
          sx={{
            ...outlinedButtonSx,
            height: 36,
            minHeight: 36,
            backgroundColor: "#74788d",
            color: AH_WHITE,
            borderColor: "#74788d",
            "&:hover": { backgroundColor: "#636678", borderColor: "#636678", boxShadow: "none" },
          }}
        >
          Close
        </Button>
      </Box>
    </Dialog>

    <Dialog
      open={Boolean(pendingArchive)}
      onClose={closeArchiveConfirm}
      fullWidth
      maxWidth="sm"
      aria-labelledby="archive-workorder-title"
      PaperProps={{
        sx: {
          ...auditHubPageSx,
          borderRadius: "12px",
          overflow: "hidden",
          maxWidth: 520,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          minHeight: 56,
          px: 3,
          backgroundColor: AH_ACCENT,
        }}
      >
        <Typography
          id="archive-workorder-title"
          sx={{ fontWeight: 700, fontSize: "1.0625rem", color: AH_WHITE, lineHeight: 1.3 }}
        >
          Archive Workorder
        </Typography>
        <Box
          component="button"
          type="button"
          aria-label="Close"
          disabled={archiveBusy}
          onClick={closeArchiveConfirm}
          sx={{
            p: 0,
            m: 0,
            border: "none",
            background: "transparent",
            color: AH_WHITE,
            fontSize: 28,
            fontWeight: 700,
            lineHeight: 1,
            cursor: archiveBusy ? "default" : "pointer",
            flexShrink: 0,
          }}
        >
          ×
        </Box>
      </Box>
      <DialogContent sx={{ px: 3, py: 2.5, backgroundColor: AH_WHITE }}>
        <Typography sx={{ fontSize: "0.875rem", color: AH_TEXT, lineHeight: 1.6, overflowWrap: "anywhere" }}>
          Archive{" "}
          <Box component="span" sx={{ fontWeight: 600 }}>
            {pendingLabel}
          </Box>
          ?
        </Typography>
        <Typography sx={{ mt: 1, fontSize: "0.8125rem", color: AH_MUTED, lineHeight: 1.6 }}>
          This removes it from the active workorders list.
        </Typography>
      </DialogContent>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1.5,
          px: 3,
          py: 2,
          borderTop: `1px solid ${AH_BORDER}`,
          backgroundColor: "#f8fafc",
        }}
      >
        <Button
          variant="contained"
          onClick={closeArchiveConfirm}
          disabled={archiveBusy}
          sx={{
            ...outlinedButtonSx,
            height: 36,
            minHeight: 36,
            backgroundColor: "#74788d",
            color: AH_WHITE,
            borderColor: "#74788d",
            "&:hover": { backgroundColor: "#636678", borderColor: "#636678", boxShadow: "none" },
            "&.Mui-disabled": { backgroundColor: "#74788d", color: AH_WHITE, opacity: 0.65 },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={confirmArchive}
          disabled={archiveBusy}
          startIcon={archiveBusy ? <CircularProgress size={14} color="inherit" /> : null}
          sx={{ ...primaryButtonSx, height: 36, minHeight: 36, minWidth: 120 }}
        >
          {archiveBusy ? "Archiving…" : "Archive"}
        </Button>
      </Box>
    </Dialog>
    </>
  );
};

export default TaskOrdersDialog;
